import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, Home, Save, Plus, Trash2, Edit2 } from 'lucide-react';

const AdminFAQ = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchContent();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/faq-content', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('İçerik yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Çıkış yapıldı');
  };

  const handleChange = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  const handleFaqChange = (index, field, value) => {
    const faqs = [...content.faqs];
    faqs[index] = { ...faqs[index], [field]: value };
    setContent({ ...content, faqs });
  };

  const addFaq = () => {
    const faqs = [...content.faqs, {
      question: 'Yeni Soru',
      answer: '',
      category: 'Genel'
    }];
    setContent({ ...content, faqs });
  };

  const removeFaq = (index) => {
    const faqs = content.faqs.filter((_, i) => i !== index);
    setContent({ ...content, faqs });
  };

  const moveFaq = (index, direction) => {
    const faqs = [...content.faqs];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < faqs.length) {
      [faqs[index], faqs[newIndex]] = [faqs[newIndex], faqs[index]];
      setContent({ ...content, faqs });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/faq-content', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
      });

      if (response.ok) {
        toast.success('İçerik kaydedildi');
      } else {
        toast.error('Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-[#1e4a7a]">MİGA Yapı - Admin Panel</h1>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Siteyi Görüntüle
                </Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4 overflow-x-auto">
            <Link to="/admin/dashboard" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              Ana Sayfa İçeriği
            </Link>
            <Link to="/admin/about" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              Kurumsal
            </Link>
            <Link to="/admin/services" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              Hizmetlerimiz
            </Link>
            <Link to="/admin/contact" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              İletişim
            </Link>
            <Link to="/admin/faq" className="text-[#1e4a7a] font-medium border-b-2 border-[#1e4a7a] pb-4 whitespace-nowrap">
              SSS
            </Link>
            <Link to="/admin/projects" className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap">
              Proje Yönetimi
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">SSS Sayfa İçeriği</h2>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Header Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Başlık Bölümü</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Sayfa Başlığı</Label>
                  <Input
                    value={content?.title || ''}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label>Alt Başlık</Label>
                  <Textarea
                    value={content?.subtitle || ''}
                    onChange={(e) => handleChange('subtitle', e.target.value)}
                    rows={2}
                    required
                  />
                </div>
              </div>
            </div>

            {/* FAQs */}
            <div className="border-b pb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Sorular & Cevaplar ({content?.faqs?.length || 0})</h3>
                <Button type="button" onClick={addFaq} variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Soru Ekle
                </Button>
              </div>
              <div className="space-y-4">
                {content?.faqs?.map((faq, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-semibold">Soru {index + 1}</h4>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          onClick={() => moveFaq(index, 'up')}
                          variant="outline"
                          size="sm"
                          disabled={index === 0}
                        >
                          ↑
                        </Button>
                        <Button
                          type="button"
                          onClick={() => moveFaq(index, 'down')}
                          variant="outline"
                          size="sm"
                          disabled={index === content.faqs.length - 1}
                        >
                          ↓
                        </Button>
                        {content.faqs.length > 1 && (
                          <Button
                            type="button"
                            onClick={() => removeFaq(index)}
                            variant="outline"
                            size="sm"
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label>Kategori</Label>
                        <select
                          value={faq.category}
                          onChange={(e) => handleFaqChange(index, 'category', e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                          required
                        >
                          <option value="Genel">Genel</option>
                          <option value="Kentsel Dönüşüm">Kentsel Dönüşüm</option>
                          <option value="Proje Süreci">Proje Süreci</option>
                          <option value="Kalite">Kalite</option>
                          <option value="Fiyatlandırma">Fiyatlandırma</option>
                        </select>
                      </div>
                      <div>
                        <Label>Soru</Label>
                        <Input
                          value={faq.question}
                          onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Cevap</Label>
                        <Textarea
                          value={faq.answer}
                          onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                          rows={4}
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-6 border-t">
              <Button type="submit" disabled={saving} className="bg-[#dc2626] hover:bg-[#b91c1c]">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AdminFAQ;
